import React from "react";
import styled from "styled-components";

import { partitionBy } from "@examspark/common";
import { Label, SelectInput, Toolbar } from "@examspark/common-ui";
import { StyledCommonProps } from "@examspark/common-ui/dist/utils/styled";
import {
  getObjectiveCategory,
  getObjectiveCurriculumUsage,
} from "@examspark/customer/tcmpc";
import {
  Objective,
  ObjectivesItem,
  TeksCategory,
  TeksCategoryList,
  TeksCategoryMeta,
  TeksCurriculumUsage,
  TeksCurriculumUsageList,
  TeksCurriculumUsageMeta,
} from "@examspark/types";

export const StyledObjectivesFilterDropdown = styled.div`
  width: 30rem;
  flex-grow: 0;
  flex-shrink: 1;
`;

const Styled = styled(Toolbar)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  // this would be way better as a container query,
  // once we update styled-components to v6
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

type UsageOption = {
  label: string;
  value: TeksCurriculumUsage;
  type: "usages";
};
type CategoryOption = {
  label: string;
  value: TeksCategory;
  type: "categories";
};
type UsageOrCategoryOption = UsageOption | CategoryOption;

export type ObjectiveFilterState = readonly UsageOrCategoryOption[];

const _ObjectivesFilter = ({
  filterOptions,
  setFilterOptions,
  filtersFor,
  ...props
}: {
  filterOptions: readonly UsageOrCategoryOption[];
  setFilterOptions: (options: readonly UsageOrCategoryOption[]) => void;
  filtersFor: "objectives" | "objectivesItems";
  availableCategories?: readonly TeksCategory[];
  availableUsages?: readonly TeksCurriculumUsage[];
} & StyledCommonProps) => {
  const categories = filterOptions.filter((o) => o.type === "categories");
  const usages = filterOptions.filter((o) => o.type === "usages");

  return (
    <Styled className={props.className}>
      <StyledObjectivesFilterDropdown>
        <Label label="Categories">
          <SelectInput
            isMulti
            useCheckboxesForMultiSelect
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            value={categories.map((p) => p.value)}
            onChange={(options) => setFilterOptions([...options, ...usages])}
            options={[
              {
                label: "Category",
                options: (props.availableCategories ?? TeksCategoryList).map(
                  (v) => ({
                    label: TeksCategoryMeta[v].label,
                    value: v,
                    type: "categories",
                  }),
                ) as CategoryOption[],
              },
            ]}
          />
        </Label>
      </StyledObjectivesFilterDropdown>
      {filtersFor === "objectivesItems" && (
        <StyledObjectivesFilterDropdown>
          <Label label="Curriculum Usage">
            <SelectInput
              isMulti
              useCheckboxesForMultiSelect
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              value={usages.map((p) => p.value)}
              onChange={(options) =>
                setFilterOptions([...options, ...categories])
              }
              options={[
                {
                  label: "Curriculum Usage",
                  options: (
                    props.availableUsages ?? TeksCurriculumUsageList
                  ).map((v) => ({
                    label: TeksCurriculumUsageMeta[v].label,
                    value: v,
                    type: "usages",
                  })) as UsageOption[],
                },
              ]}
            />
          </Label>
        </StyledObjectivesFilterDropdown>
      )}
    </Styled>
  );
};

export const ObjectivesFilter = styled(_ObjectivesFilter)``;

export const filterObjectivesItems = (
  filterOptions: ObjectiveFilterState,
  objectives: ObjectivesItem[],
) => {
  const { usages, categories } = partitionBy(
    filterOptions,
    (o) => o.type,
    (o) => o.value,
  );

  return (
    objectives?.filter((o) => {
      const usage = getObjectiveCurriculumUsage(o);
      const category = getObjectiveCategory(o.objective);

      return (
        (usage == null || usages?.includes(usage) !== false) &&
        (category == null || categories?.includes(category) !== false)
      );
    }) ?? []
  );
};

export const filterObjectives = <T extends Objective>(
  filterOptions: ObjectiveFilterState,
  objectives: T[],
) => {
  const { categories } = partitionBy(
    filterOptions,
    (o) => o.type,
    (o) => o.value,
  );

  let result = objectives ?? [];

  if (categories?.length) {
    result = result.filter((o) => {
      const category = getObjectiveCategory(o);
      return category != null && categories.includes(category);
    });
  }

  return result;
};
